import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderOpen, Archive, RotateCcw, Trash2, ArrowUpRight, Lock, Search, Copy, FolderKanban, PoundSterling, Wallet, FileText, Loader2, Sparkles, Pencil, Plug } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import TradePackNudge from "@/components/TradePackNudge";
import RegionNudge from "@/components/RegionNudge";
import { track } from "@/lib/analytics";

const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const xeroChipColor = (label) => {
  const l = (label || "").toLowerCase();
  if (l === "paid") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (l === "approved" || l === "part-paid") return "bg-blue-50 text-blue-700 border-blue-200";
  if (l === "awaiting approval") return "bg-amber-50 text-amber-700 border-amber-200";
  if (l === "voided" || l === "deleted") return "bg-red-50 text-red-600 border-red-200";
  return "bg-slate-50 text-slate-600 border-slate-200"; // Draft
};

const Dashboard = () => {
  const { refreshMe, user } = useAuth();
  const isViewer = user?.role === "viewer";
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [sub, setSub] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", client_email: "", address: "" });
  const [editingId, setEditingId] = useState(null);
  const [xeroChips, setXeroChips] = useState({});
  const [creating, setCreating] = useState(false);
  const [limitDialog, setLimitDialog] = useState(false);
  const [query, setQuery] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [sampling, setSampling] = useState(false);

  const createSample = async () => {
    setSampling(true);
    try {
      const { data } = await api.post("/projects/sample");
      track("sample_project_created", {});
      await load();
      navigate(`/app/project/${data.id}`);
    } catch (err) {
      if (err.response?.status === 402) setLimitDialog(true);
      else toast.error(apiError(err.response?.data?.detail));
    } finally { setSampling(false); }
  };

  const load = useCallback(async () => {
    const [p, s, sum] = await Promise.all([
      api.get("/projects"), api.get("/billing/subscription"), api.get("/dashboard/summary"),
    ]);
    setProjects(p.data);
    setSub(s.data);
    setSummary(sum.data);
    api.get("/integrations/xero/invoice-summary")
      .then(({ data }) => setXeroChips(Object.fromEntries((data.items || []).map((i) => [i.project_id, i]))))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const matches = (p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [p.name, p.client, p.address].some((v) => (v || "").toLowerCase().includes(q));
  };
  const active = projects.filter((p) => p.status === "active" && matches(p));
  const archived = projects.filter((p) => p.status === "archived" && matches(p));
  const activeCount = projects.filter((p) => p.status === "active" && !p.is_sample).length;
  const limit = sub?.active_limit;
  const atLimit = limit != null && activeCount >= limit;

  const openCreate = () => {
    if (atLimit) { setLimitDialog(true); return; }
    setEditingId(null);
    setForm({ name: "", client: "", client_email: "", address: "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name || "", client: p.client || "", client_email: p.client_email || "", address: p.address || "" });
    setOpen(true);
  };

  const save = async () => {
    setCreating(true);
    try {
      if (editingId) {
        await api.patch(`/projects/${editingId}`, form);
        setOpen(false);
        toast.success("Project updated");
        await load();
      } else {
        const { data } = await api.post("/projects", form);
        setOpen(false);
        await load();
        navigate(`/app/project/${data.id}`);
      }
    } catch (err) {
      if (err.response?.status === 402) { setOpen(false); setLimitDialog(true); }
      else toast.error(apiError(err.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const archive = async (id) => { await api.post(`/projects/${id}/archive`); toast.success("Project archived"); load(); refreshMe(); };
  const reopen = async (id) => {
    try { await api.post(`/projects/${id}/reopen`); toast.success("Project reopened"); load(); }
    catch (err) { if (err.response?.status === 402) setLimitDialog(true); else toast.error(apiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/projects/${id}`); toast.success("Project deleted"); setToDelete(null); load(); refreshMe(); };
  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/projects/${id}/duplicate`);
      toast.success("Project duplicated");
      await load();
      navigate(`/app/project/${data.id}`);
    } catch (err) {
      if (err.response?.status === 402) setLimitDialog(true);
      else toast.error(apiError(err.response?.data?.detail));
    }
  };

  const kpis = [
    { label: "Active projects", value: summary?.active_projects ?? 0, icon: FolderKanban, sub: `${summary?.archived_projects ?? 0} archived` },
    { label: "Estimate value", value: gbp(summary?.estimate_value), icon: FileText, sub: "across all jobs" },
    { label: "Billed to date", value: gbp(summary?.total_billed), icon: PoundSterling, sub: "completed work" },
    { label: "Outstanding due", value: gbp(summary?.amount_due), icon: Wallet, sub: "net of retention" },
  ];

  return (
    <AppShell>
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-6 md:px-8 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Projects</h1>
            <p className="text-sm text-slate-500 mt-0.5" data-testid="usage-indicator">
              {activeCount} active {limit != null ? `of ${limit}` : "· unlimited"} · {summary?.archived_projects ?? 0} archived
            </p>
          </div>
          {!isViewer && (
            <Button onClick={openCreate} data-testid="new-project-btn" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-1.5" /> New project
            </Button>
          )}
        </div>
        {atLimit && (
          <div className="px-6 md:px-8 py-3 bg-amber-50 border-t border-amber-200 flex items-center justify-between" data-testid="limit-banner">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <Lock className="h-4 w-4" /> You've hit your active project limit. Archive a job or upgrade to add more.
            </p>
            <Button size="sm" variant="outline" onClick={() => navigate("/app/account")} className="border-amber-300 text-amber-800">
              Upgrade <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <div className="p-6 md:p-8 space-y-8">
        <TradePackNudge />
        <RegionNudge />
        <OnboardingChecklist projects={projects} onCreateProject={openCreate} />

        {/* KPI summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-summary">
          {kpis.map((k) => (
            <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`kpi-${k.label.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-semibold">
                <k.icon className="h-4 w-4" /> {k.label}
              </div>
              <p className="font-display text-2xl font-bold text-navy mt-2">{k.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{k.sub}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="project-search"
            placeholder="Search projects by name, client or address…" className="pl-9" />
        </div>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Active</h2>
          {active.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-xl p-12 text-center bg-white">
              <FolderOpen className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="mt-3 text-slate-500">{query ? "No active projects match your search." : "No active projects yet."}</p>
              {!query && !isViewer && (
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
                  <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="empty-create-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Create your first project
                  </Button>
                  <Button onClick={createSample} disabled={sampling} variant="outline" className="border-slate-300" data-testid="empty-sample-btn">
                    {sampling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5 text-blue-600" />} Try a sample project
                  </Button>
                </div>
              )}
              {isViewer && <p className="mt-2 text-xs text-slate-400">You have read-only (Viewer) access.</p>}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {active.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow group" data-testid={`project-card-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <h3 className="font-display font-semibold text-slate-900 cursor-pointer hover:text-blue-600" onClick={() => navigate(`/app/project/${p.id}`)}>
                      {p.name}
                    </h3>
                    {p.is_sample ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 px-2 py-0.5 rounded" data-testid={`demo-badge-${p.id}`} title="Demo project — free and doesn't count towards your plan limit">Demo · free</span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 px-2 py-0.5 rounded">Active</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{p.client || "—"}</p>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{p.address || ""}</p>
                  {xeroChips[p.id] && (
                    <a href={xeroChips[p.id].xero_url || undefined} target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`xero-chip-${p.id}`}
                       className={`mt-3 inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-2.5 py-1 ${xeroChipColor(xeroChips[p.id].status_label)}`}
                       title="Open this invoice in Xero">
                      <Plug className="h-3 w-3" />
                      Xero · {xeroChips[p.id].invoice_number || "draft"} · {xeroChips[p.id].status_label}
                    </a>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" onClick={() => navigate(`/app/project/${p.id}`)} data-testid={`open-project-${p.id}`} className="bg-slate-900 hover:bg-slate-800 flex-1">Open</Button>
                    {!isViewer && <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} title="Edit details"><Pencil className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="outline" onClick={() => duplicate(p.id)} data-testid={`duplicate-project-${p.id}`} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => archive(p.id)} data-testid={`archive-project-${p.id}`} title="Archive"><Archive className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {archived.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Archived</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {archived.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5 opacity-90" data-testid={`archived-card-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <h3 className="font-display font-semibold text-slate-700">{p.name}</h3>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Archived</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{p.client || "—"}</p>
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => reopen(p.id)} data-testid={`reopen-project-${p.id}`} className="flex-1"><RotateCcw className="h-4 w-4 mr-1.5" /> Reopen</Button>
                    <Button size="sm" variant="ghost" onClick={() => setToDelete(p)} data-testid={`delete-project-${p.id}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toDelete?.name}</strong> and all its blueprints, takeoffs, estimates and billing will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(toDelete.id)} data-testid="delete-confirm-btn" className="bg-red-600 hover:bg-red-700">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-project-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit project" : "New project"}</DialogTitle>
            <DialogDescription>{editingId ? "Update the client details for this job." : "Set up a new job to start your takeoff, estimate and billing."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label htmlFor="pn">Project name</Label><Input id="pn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" className="mt-1" placeholder="e.g. Riverside Apartments — Fit-out" /></div>
            <div><Label htmlFor="pc">Client</Label><Input id="pc" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="project-client-input" className="mt-1" /></div>
            <div><Label htmlFor="pce">Client email <span className="text-slate-400 font-normal">(optional)</span></Label><Input id="pce" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="project-client-email-input" className="mt-1" placeholder="accounts@client.co.uk" /></div>
            <div><Label htmlFor="pa">Site address</Label><Input id="pa" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="project-address-input" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name || creating} data-testid="create-project-submit" className="bg-blue-600 hover:bg-blue-700">{creating ? (editingId ? "Saving…" : "Creating…") : (editingId ? "Save changes" : "Create project")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Limit dialog */}
      <Dialog open={limitDialog} onOpenChange={setLimitDialog}>
        <DialogContent data-testid="limit-dialog">
          <DialogHeader>
            <DialogTitle>Active project limit reached</DialogTitle>
            <DialogDescription>Archive a project or upgrade to add more active jobs.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Your <span className="font-semibold">{sub?.plan_name}</span> plan allows {limit} active project{limit === 1 ? "" : "s"}.
            Archive a completed job to free up space, or upgrade for more capacity.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitDialog(false)}>Got it</Button>
            <Button onClick={() => navigate("/app/account")} data-testid="limit-upgrade-btn" className="bg-blue-600 hover:bg-blue-700">See upgrade options</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Dashboard;
